import { SyncTable } from "../sync/ws-envelope";

/**
 * Per-table layout for the native client-apply path (the "fixed table layout"
 * the generic apply builder in `apply-remote.ts` reads to write an incoming
 * remote op into local SQLite). Mirrors `efb-app-server/src/sync/tables.ts`
 * (`pk` / `hasObsolete` / `hasSoftDelete` / `tenantScopedPk`); the structure is
 * drift-tested against it in lockstep (`efb-app-server/tests/sync-tables-copies.test.ts`).
 *
 * `table` is the LOCAL physical name. Every table is addressed in the SAME
 * all-lowercase form the device's own catalog (`db/catalog.ts`) and the server
 * (`efb-app-server/src/sync/tables.ts`) use. `notam`/`met` were historically
 * written here in a legacy camelCase form, but SQLite identifiers are
 * case-insensitive so it always resolved to the one lowercase table the catalog
 * reads/writes — the two casings were aliases of a single table, never a real
 * device-vs-server name divergence. Normalized to lowercase so the sync wiring,
 * the catalog, and the server all spell every table identically (the drift test
 * compares structure by SyncTable key, not the raw name, so it never caught the
 * mismatch).
 *
 * The column LIST is deliberately NOT hard-coded here. The builder intersects
 * the incoming op's row keys with the live local columns (`PRAGMA table_info`),
 * so server-stamped columns the legacy local schema never had
 * (`serverSeq`/`originDeviceId`/`createdBy`) — and any other schema skew — are
 * skipped rather than producing a `no such column` error. That keeps this
 * manifest to just identity + capability flags, with no per-column maintenance.
 */
export interface SyncTableMeta {
  /** Physical SQLite table name in the LOCAL (mobile) schema. */
  table: string;
  /** Primary-key column; the op's rowId binds to it on upsert. */
  pk: string;
  /** Supports an `obsolete` op (sets `obsolete = 1`). */
  hasObsolete: boolean;
  /** Supports a `delete` op (sets `deleted = 1`). */
  hasSoftDelete: boolean;
  /** pk is unique only within a registration → conflict/where target is `(registrationId, pk)`. */
  tenantScopedPk?: boolean;
}

export const SYNC_APPLY_TABLES: Record<SyncTable, SyncTableMeta> = {
  flightlist: {
    table: "opsctrl_flightopscontroller_flightlist",
    pk: "flightId",
    hasObsolete: false,
    hasSoftDelete: true,
  },
  flightcrew: {
    // Repeatable duty signing: pk is the deterministic rowId
    // (rowIdFor([ident, dutyFrom, created])), matching the server tables.ts +
    // the local UNIQUE index, so a pulled remote sign upserts ON CONFLICT(rowId).
    // `created` is in the key, so each sign is a distinct row and the upsert
    // branch only fires for the same sign arriving twice.
    table: "opsctrl_flightopscontroller_flightcrew",
    pk: "rowId",
    hasObsolete: false,
    hasSoftDelete: false,
  },
  fuel: { table: "opsctrl_flightopscontroller_fuel", pk: "rowId", hasObsolete: true, hasSoftDelete: false },
  loading: { table: "opsctrl_flightopscontroller_loading", pk: "rowId", hasObsolete: true, hasSoftDelete: false },
  loadingelements: {
    table: "opsctrl_flightopscontroller_loadingelements",
    pk: "rowId",
    hasObsolete: true,
    hasSoftDelete: false,
  },
  nav: { table: "opsctrl_flightopscontroller_nav", pk: "rowId", hasObsolete: true, hasSoftDelete: false },
  navseg: { table: "opsctrl_flightopscontroller_navseg", pk: "rowId", hasObsolete: true, hasSoftDelete: false },
  param: { table: "opsctrl_flightopscontroller_param", pk: "rowId", hasObsolete: false, hasSoftDelete: true },
  perf: { table: "opsctrl_flightopscontroller_perf", pk: "rowId", hasObsolete: true, hasSoftDelete: false },
  notam: { table: "opsctrl_flightopscontroller_notam", pk: "rowId", hasObsolete: true, hasSoftDelete: false },
  met: { table: "opsctrl_flightopscontroller_met", pk: "rowId", hasObsolete: true, hasSoftDelete: false },
  duties: {
    table: "opsctrl_flightcrewcontroller_duties",
    pk: "dutyId",
    hasObsolete: false,
    hasSoftDelete: false,
    tenantScopedPk: true,
  },
  crewmemberdata: {
    table: "opsctrl_flightcrewcontroller_crewmemberdata",
    pk: "rowId",
    hasObsolete: false,
    hasSoftDelete: true,
    tenantScopedPk: true,
  },
  historiccrewmembers: {
    table: "opsctrl_flightcrewcontroller_historiccrewmembers",
    pk: "crewMemberId",
    hasObsolete: false,
    hasSoftDelete: false,
  },
};

/**
 * Columns the server stamps that the legacy local schema does not carry. The
 * builder skips them when writing an incoming op (the PRAGMA intersect would
 * drop them anyway; this is the explicit, documented guard).
 */
export const SERVER_ONLY_COLUMNS: readonly string[] = ["serverSeq", "originDeviceId", "createdBy"];
